<script setup lang="ts">
import { ref, onMounted, watch, Ref } from 'vue';
import { LoggerService } from '@/utils/LoggerService';

const logger = new LoggerService('PWAUpdate');
const isRefreshNeeded: Ref<boolean> = ref(false);
const serviceWorker: Ref<ServiceWorkerRegistration | null> =
  ref<ServiceWorkerRegistration | null>(null);

const updateServiceWorker: () => Promise<void> = async () => {
  if (!serviceWorker.value?.waiting) return;

  try {
    // Send message to service worker to skip waiting and activate new version
    serviceWorker.value.waiting.postMessage({ type: 'SKIP_WAITING' });
    isRefreshNeeded.value = false;

    // Reload the page to load the new version
    window.location.reload();
  } catch (error) {
    logger.error('Error updating service worker:', error);
    // If postMessage fails (e.g., invalid origin), just reload to get new version
    window.location.reload();
  }
};

onMounted(() => {
  if ('serviceWorker' in navigator) {
    // First check if there's an existing registration before using ready
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => {
        if (registrations.length > 0) {
          // Use existing registration
          return navigator.serviceWorker.ready;
        } else {
          // No existing registration, service worker may not be registered yet or failed to load
          logger.log('No service worker registration found - may be still loading or failed');
          return null;
        }
      })
      .then((reg) => {
        if (!reg) return;
        
        serviceWorker.value = reg;
        logger.log('Service Worker is ready');

        // Listen for new installing workers
        reg.addEventListener('installing', (event) => {
          logger.log('Service Worker installing:', event);
        });

        // Listen for state changes during installation
        reg.addEventListener('installed', (event) => {
          logger.log('Service Worker installed:', event);
        });

        // Listen for activation
        reg.addEventListener('activate', (event) => {
          logger.log('Service Worker activated:', event);
        });

        // Watch for new waiting service worker
        watch(
          () => serviceWorker.value?.waiting,
          (newWorker: ServiceWorker | null | undefined) => {
            if (newWorker) {
              logger.log('New service worker detected, updating...');
              updateServiceWorker();
            }
          },
        );
      })
      .catch((error: any) => {
        // Silently handle service worker load failures - these are expected in certain scenarios
        // (network issues, script not found, CORS, etc.) and shouldn't be sent to Sentry
        const errorMessage: string = error?.message || String(error);
        if (errorMessage.includes('sw.js') || errorMessage.includes('service worker') || errorMessage.includes('load failed')) {
          logger.log('Service Worker script failed to load (expected in some scenarios):', errorMessage);
          // Don't log to Sentry - this is expected behavior
        } else {
          logger.error('Service Worker registration error:', error);
        }
      });
  }
});
</script>
<template>
  <div v-if="isRefreshNeeded" class="pwa-toast"></div>
</template>
